import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { CreditCard, ShieldCheck } from 'lucide-react';
import { useOrg } from '../../hooks/useOrg';
import { api } from '../../lib/api';
import { formatPricePerLocation } from '../../lib/pipStates';
import { TRIAL_DAYS } from '../../lib/trial';
import PageLoader from '../shared/PageLoader';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export default function SubscribePage() {
  const { currentOrg } = useOrg();
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // PIP/no-fault states are billed at a higher per-location rate.
  const perLocationPrice = formatPricePerLocation(currentOrg?.state, currentOrg?.provider_type);

  useEffect(() => {
    if (!currentOrg) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.createCheckoutSession(currentOrg.id)
      .then(({ client_secret }) => {
        if (!cancelled) setClientSecret(client_secret);
      })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currentOrg]);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #efeef8 0%, #ecebf6 50%, #ecebf6 100%)',
        padding: '56px 24px 96px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#0da7ca', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600, marginBottom: 16 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#0da7ca' }} />
            Activate subscription
          </div>
          <h1 style={{
            fontFamily: '"Bricolage Grotesque", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            fontSize: 'clamp(32px, 4vw, 44px)',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            margin: '0 0 16px',
            color: '#0b0b14',
          }}>
            One step from <em style={{ color: '#0da7ca', fontWeight: 400 }}>going live</em>.
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: 'rgba(11,11,20,0.55)', margin: 0, maxWidth: 480, marginInline: 'auto' }}>
            {currentOrg?.name} needs an active MediLink Pro subscription to access the platform. Cancel anytime from billing settings.
          </p>
        </div>

        {/* Plan summary */}
        <div style={{
          background: 'white',
          border: '1px solid rgba(11,11,20,0.08)',
          borderRadius: 16,
          padding: '20px 24px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          boxShadow: '0 1px 0 rgba(0,0,0,0.02), 0 8px 24px -8px rgba(11,11,20,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 12,
              background: 'rgba(13,167,202,0.10)', color: '#0da7ca',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <CreditCard size={18} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0b0b14' }}>MediLink Pro</div>
              <div style={{ fontSize: 12, color: 'rgba(11,11,20,0.55)', marginTop: 2 }}>
                {TRIAL_DAYS} days free · no card to start · cancel anytime
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            <span style={{
              fontFamily: '"Bricolage Grotesque", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              fontSize: 28, fontWeight: 700, color: '#0b0b14',
            }}>{perLocationPrice}</span>
            <span style={{ fontSize: 12, color: 'rgba(11,11,20,0.55)', marginLeft: 4 }}>/location/mo after your free days</span>
          </div>
        </div>

        {/* Embedded Stripe Checkout — renders inside our page, no redirect. */}
        <div style={{
          background: 'white',
          border: '1px solid rgba(11,11,20,0.08)',
          borderRadius: 16,
          minHeight: 540,
          overflow: 'hidden',
          boxShadow: '0 1px 0 rgba(0,0,0,0.02), 0 24px 48px -16px rgba(11,11,20,0.10)',
        }}>
          {loading && (
            <div style={{ minHeight: 540, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PageLoader variant="block" sub="Stripe" label="Loading secure checkout…" />
            </div>
          )}
          {error && (
            <div style={{ padding: 24, color: '#b91c1c', fontSize: 13 }}>
              Couldn't load checkout: {error}
            </div>
          )}
          {clientSecret && (
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 24, color: 'rgba(11,11,20,0.55)', fontSize: 12 }}>
          <ShieldCheck size={14} />
          {`No card needed to start. Add one any time before your ${TRIAL_DAYS} free days end — if you don't, your account simply pauses. Nothing is charged.`}
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
